/**
 * LogInteractionModal — log a customer interaction (call, text, email, note)
 * without leaving the current surface.
 *
 * Appends a CustomerCommEntry; caller passes the customerName so we can find
 * matching jobs. If a `relatedJobId` is provided we attach it directly.
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, MessageSquare, Mail, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import type { CommType, CustomerCommEntry } from "@/data/types";

export interface LogInteractionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Customer key — used to associate the entry. */
  customerName: string;
  /** Optional job link. */
  relatedJobId?: string;
  /** Optional pre-selected interaction type. */
  defaultType?: CommType;
  /** Optional prefilled note body — used when an upstream engine (e.g. Maverick) has a drafted message. */
  defaultNote?: string;
  /** Optional success callback — fires only after the interaction is committed (Confirm clicked + note valid).
   *  Distinct from `onOpenChange(false)` which fires on dismiss/cancel as well. */
  onLogged?: (entry: CustomerCommEntry) => void;
}

const TYPES: { value: CommType; label: string; icon: React.ElementType }[] = [
  { value: "Phone Call", label: "Phone Call", icon: Phone },
  { value: "Text", label: "Text Message", icon: MessageSquare },
  { value: "Email", label: "Email", icon: Mail },
  { value: "Internal Note", label: "Internal Note", icon: StickyNote },
];

const OUTCOMES = [
  "Reached customer",
  "Left voicemail",
  "No answer",
  "Customer requested call back",
  "Confirmed appointment",
  "Rescheduled",
  "Other",
] as const;

export function LogInteractionModal({
  open,
  onOpenChange,
  customerName,
  relatedJobId,
  defaultType = "Phone Call",
  defaultNote,
  onLogged,
}: LogInteractionModalProps) {
  const { customerComms, setCustomerComms } = useAppData();
  const { role } = useRole();
  const [type, setType] = useState<CommType>(defaultType);
  const [outcome, setOutcome] = useState<string>(OUTCOMES[0]);
  const [note, setNote] = useState("");
  const [nextStep, setNextStep] = useState("");

  useEffect(() => {
    if (open) {
      setType(defaultType);
      setOutcome(OUTCOMES[0]);
      setNote(defaultNote ?? "");
      setNextStep("");
    }
  }, [open, defaultType, defaultNote]);

  const handleConfirm = () => {
    if (!note.trim()) {
      toast.error("Add a note describing what happened");
      return;
    }
    const now = new Date();
    const summary = `${outcome}${note ? ` — ${note}` : ""}${nextStep ? ` · Next: ${nextStep}` : ""}`;
    const entry: CustomerCommEntry = {
      id: `comm-${Date.now()}`,
      date: now.toISOString().slice(0, 10),
      time: now.toTimeString().slice(0, 5),
      commType: type,
      summary,
      relatedJobId,
      user: role || "User",
      source: "Manual",
      origin: "manual",
    };
    setCustomerComms((prev) => [entry, ...(prev ?? [])]);
    toast.success("Interaction logged");
    onLogged?.(entry);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" /> Log Interaction
          </DialogTitle>
          <DialogDescription>{customerName}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as CommType)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Outcome</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OUTCOMES.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="li-note" className="text-xs">Note <span className="text-destructive">*</span></Label>
            <Textarea
              id="li-note"
              placeholder="What was discussed?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="mt-1 resize-none"
            />
          </div>

          <div>
            <Label htmlFor="li-next" className="text-xs">Next Step (optional)</Label>
            <Textarea
              id="li-next"
              placeholder="Send quote tomorrow / Confirm install date / etc."
              value={nextStep}
              onChange={(e) => setNextStep(e.target.value)}
              rows={1}
              className="mt-1 resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleConfirm}>Log Interaction</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
